import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mountain, Clock, Dumbbell, MapPin } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { IMG } from "@/data/platform";

const TREK_IMG = {
  ebc: "https://static.prod-images.emergentagent.com/jobs/77d56019-0161-4f60-8d40-94609a7025f5/images/90d0d4ba6bbb24ec2b02d6f236d32ff4ea5d8e38cd1f10360d529bbc75e042d1.jpeg",
  chadar: "https://static.prod-images.emergentagent.com/jobs/77d56019-0161-4f60-8d40-94609a7025f5/images/7ae02254b5d3b337ef3212d090b1994a57b5cf30efc9886bca3a1c944c811fb4.jpeg",
  himalayan: "https://static.prod-images.emergentagent.com/jobs/77d56019-0161-4f60-8d40-94609a7025f5/images/0c7826f1297b2099ad8ddf4d873571a9b983b887442e626990086ee9c9c5e3c5.jpeg",
};

const PROGRAMS = [
  { id: "ebc", name: "Everest Base Camp", location: "Nepal Himalaya · 5,364 m", window: "12–16 weeks out", difficulty: "Demanding", image: TREK_IMG.ebc,
    blurb: "The bucket-list trek. Your plan builds time-on-feet under load, calf and core strength, and Zone 2 engine — the three things that get people to Base Camp smiling." },
  { id: "ladakh_marathon", name: "Ladakh Marathon", location: "Leh · 3,500 m", window: "14–16 weeks out", difficulty: "Demanding", image: IMG.ladakh,
    blurb: "The world's highest marathon. Heart-rate-based run progressions plus strength work, with an acclimatisation protocol built into the plan's coaching tips.", dest: "ladakh" },
  { id: "valley_of_flowers", name: "Valley of Flowers", location: "Uttarakhand · 3,858 m", window: "8 weeks out", difficulty: "Moderate", image: IMG.rishikesh,
    blurb: "UNESCO alpine meadows with one honest climb to Hemkund. Stair volume, downhill control and wet-trail readiness.", dest: "rishikesh" },
  { id: "sandakphu", name: "Sandakphu Trek", location: "Darjeeling ridge · 3,636 m", window: "8–10 weeks out", difficulty: "Moderate", image: IMG.darjeeling,
    blurb: "Sleeping-giants views of four 8,000ers. Back-to-back walking days are the test — your plan rehearses exactly that.", dest: "darjeeling" },
  { id: "chadar", name: "Chadar Frozen River", location: "Zanskar · winter", window: "10–12 weeks out", difficulty: "Demanding", image: TREK_IMG.chadar,
    blurb: "Walking a frozen river at −15 °C. Balance, ankle stability, grip and graduated cold exposure — a genuinely different prep.", dest: "ladakh" },
  { id: "himalayan", name: "Any Himalayan Trek", location: "Pick your trail", window: "8–12 weeks out", difficulty: "Adaptable", image: TREK_IMG.himalayan,
    blurb: "Goecha La, Markha Valley, Kedarkantha or your own plan — the universal mountain-prep template: engine, legs, loaded carries.", dest: "sikkim" },
];

export default function TrainBeforeTravel() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2D372B]">
      <Nav />
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 md:pb-28">
        <div className="max-w-2xl mb-12">
          <div className="overline mb-4">Train Before You Travel</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]">
            Arrive <span className="italic text-[#AD6A48]">ready.</span>
          </h1>
          <p className="mt-5 text-[#5C665A] leading-relaxed">
            Pick your goal and get a free 7-day training structure built by Coach Sanjay's plan engine — adapted to your body, equipment, experience and heart-rate zones. Repeat it weekly, progress it, arrive strong.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PROGRAMS.map((p) => (
            <div key={p.id} data-testid={`trek-program-${p.id}`} className="group bg-white border border-[#E5E2D9] rounded-3xl overflow-hidden hover:border-[#89937C] transition-colors flex flex-col">
              <div className="relative aspect-[16/9] overflow-hidden">
                <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1 rounded-full bg-[#2D372B]/85 text-[#F7F5F0]">{p.difficulty}</span>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <h3 className="font-serif-display text-2xl leading-snug">Get Ready for {p.name}</h3>
                <p className="text-xs text-[#89937C] font-medium mt-1 inline-flex items-center gap-1"><MapPin size={11} /> {p.location}</p>
                <p className="text-sm text-[#5C665A] mt-3 leading-relaxed flex-1">{p.blurb}</p>
                <div className="mt-4 flex items-center gap-2 text-xs text-[#5C665A]"><Clock size={12} className="text-[#AD6A48]" /> {p.window}</div>
                <Link to={`/plan?trek=${p.id}`} data-testid={`trek-plan-cta-${p.id}`} className="mt-4 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#2D372B] text-[#F7F5F0] text-sm font-medium hover:bg-[#89937C] transition-colors">
                  <Dumbbell size={14} /> Build my free prep plan
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 bg-white border border-[#E5E2D9] rounded-3xl p-8 sm:p-10 grid md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="overline mb-2">Not trekking?</div>
            <h2 className="font-serif-display text-3xl">The generator does everything else too.</h2>
            <p className="text-sm text-[#5C665A] mt-3">Weight loss, muscle, endurance, VO2 max — same engine, same free 7-day structure with heart-rate zones and form videos.</p>
          </div>
          <div className="flex md:justify-end gap-3 flex-wrap">
            <Link to="/plan" data-testid="general-plan-cta" className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 border border-[#89937C] text-[#89937C] font-medium hover:bg-[#89937C] hover:text-white transition-colors">
              <Mountain size={15} /> Open the plan generator
            </Link>
            <Link to="/journey" className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 bg-[#C57B57] text-white font-medium hover:bg-[#AD6A48] transition-colors">
              Plan the trip itself <ArrowUpRight size={15} />
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
